import { buildWeek } from "@features/helpers";
import { ILessonsContract } from "@features/lessons/model";
import { StreakApi } from "@features/shared/api/streak.api";
import { IStreakPeriod, IStreakStats, IWeekDay } from "@features/shared/entities/streak";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEffect, createEvent, createStore, sample } from "effector";
import { pending, reset } from "patronum";

const storesReset = createEvent();
const statsChanged = createEvent<IStreakStats>();
const getStreakListFx = createEffect<void, IStreakPeriod[] | null>();
const getStreakStatsFx = createEffect<void, IStreakStats>();

const $week = createStore<IWeekDay[]>([]);
const $streakStats = createStore<IStreakStats | null>(null).on(statsChanged, (_, stats) => stats);
const $isStreakLoading = pending([getStreakListFx, getStreakStatsFx]);

getStreakListFx.use(async () => {
    return await StreakApi.getStreakList();
});

getStreakStatsFx.use(async () => {
    return await StreakApi.getStreakStats();
});

sample({
    clock: getStreakListFx.doneData,
    fn: buildWeek,
    target: $week
});

sample({
    clock: getStreakStatsFx.doneData,
    target: $streakStats
});

const contract = new ModelContract(
    { $week, $isStreakLoading, $streakStats },
    { storesReset, statsChanged },
    { getStreakListFx, getStreakStatsFx },
    {}
);

export type IStreakContract = typeof contract;

export class StreakModule extends ModuleDescription<IStreakContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public lessonsModule: ILessonsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: [this.userAuthModule.events.authenticated, this.lessonsModule.events.lessonCompletionTracked],
            target: [getStreakStatsFx, getStreakListFx]
        });

        sample({
            clock: this.userAuthModule.events.logoutStudent,
            target: storesReset
        });

        reset({
            clock: storesReset,
            target: [$week, $streakStats]
        });
    }
}
